import {BasicDataTable, BasicDataTableConfig, DataTablesDataSource} from "./basicDataTable";
import {RequestParam} from "@/shared/api";
import {DataTableBulkSelector} from "./dataTableBulkSelector";
import {mergeProperties} from "@/shared/utils/misc";

export interface SelectableDataTableConfig extends BasicDataTableConfig {
	allowSelect?: boolean;
	selectCountElement?: JQuery;
}

/**
 * {@link BasicDataTable} with bulk selection keyed by the row id type `ID`. The
 * id requirement only applies to tables that actually select; plain
 * BasicDataTable rows need no id.
 */
export abstract class SelectableDataTable<
	T extends { id: ID },
	ID extends string | number = T["id"],
	B extends DataTableBulkSelector<T, ID> = DataTableBulkSelector<T, ID>
> extends BasicDataTable<T> {

	public bulkSelector: B;

	constructor(dataSource: DataTablesDataSource<T>, public config: SelectableDataTableConfig) {
		super(dataSource, config);

		this.bulkSelector = this.createBulkSelector();
		$(this.bulkSelector).on(DataTableBulkSelector.EVENT_BULK_SELECTION_CHANGED, () => {
			this.updateSelectedRowsCount();
		});
	}

	protected abstract createBulkSelector(): B;

	protected override createBasicConfig(): DataTables.Settings {
		const selectSetting: string | boolean | DataTables.SelectSettings = this.config.allowSelect ? {
			style: "multi",
			selector: "td:first-child",
			info: false
		} : false;

		return mergeProperties(super.createBasicConfig(), {
			select: selectSetting,
			// the buttons "checkbox" option is missing from the upstream typings
			buttons: {
				checkbox: {
					select: true
				}
			}
		});
	}

	public override filter(filterData: RequestParam[]): JQueryPromise<T[]> {
		this.bulkSelector.reset();
		return super.filter(filterData);
	}

	/* Runs during super() before bulkSelector is assigned, hence the guard. */
	protected override rowCallback(row: Node, recording: T, index: number) {
		if (this.bulkSelector?.isSelected(recording.id)) {
			this.dataTable.rows(index).select();
		}
	}

	protected updateSelectedRowsCount() {
		const selectedRowsCount = this.bulkSelector.getSelectedEntriesCount();
		const rowCountElement = this.config.selectCountElement || $();

		if (this.bulkSelector.isAllEntriesSelected() && !this.bulkSelector.isNoneSelected()) {
			rowCountElement.text(messages.withArguments("bulkselection.allselected", selectedRowsCount));
			this.bulkSelector.selectAllCheckbox();
		} else if (this.bulkSelector.isNoneSelected()) {
			rowCountElement.text("");
			this.bulkSelector.unSelectAllCheckbox();
		} else if (this.bulkSelector.getSelectedEntriesCount() === 1) {
			rowCountElement.text(messages["bulkselection.oneselected"]);
			this.bulkSelector.unSelectAllCheckbox();
		} else {
			rowCountElement.text(messages.withArguments("bulkselection.entriesselected", selectedRowsCount));
			this.bulkSelector.unSelectAllCheckbox();
		}
	}
}
